import { useStep } from 'usehooks-ts';
import { useEffect, useState } from 'react';

import { LoadingSpinner, ScrollArea } from '@app/shared/ui/atoms/Common';
import {
  TopBar,
  SlideOneContent,
  SlideTwoContent,
  SlideThreeContent,
  CarouselFooter,
} from '@app/shared/ui/organisms/DailyEnergy';
import { SLIDES_COUNT } from '@app/shared/constants/dailyEnergy';
import { usePreloadImages } from '@app/shared/hooks/usePreloadImages';
import { useSettlementGate } from '@app/shared/hooks/useSettlementGate';
import { DAILY_ENERGY_PRELOAD_IMAGES } from '@app/assets';
import { LOCAL_STORAGE, ONE_WEEK_MS } from '@app/shared/constants/common';
import { BuySettlementModal } from '@app/shared/ui/organisms/Common';
import { useTranslation } from 'react-i18next';
import { formatImgSrc, getMoonPhaseAndDay } from '@app/shared/utils/dailyEnergy';
import { MoonDayData, MoonPhaseData } from '@app/shared/types/pages/dailyEnergy';

export default function DailyEnergyPage() {
  const { t } = useTranslation();
  const {
    requiredSettlement,
    isOpen,
    handlePurchase,
    isPurchasingSettlement,
    isSettlementsLoading,
  } = useSettlementGate(0, LOCAL_STORAGE.energyUsedAt, ONE_WEEK_MS);

  const [
    currentSlide,
    {
      goToPrevStep: goToPrevSlide,
      canGoToPrevStep: canGoToPrevSlide,
      goToNextStep: goToNextSlide,
      canGoToNextStep: canGoToNextSlide,
    },
  ] = useStep(SLIDES_COUNT);

  const [moonData, setMoonData] = useState({ phase: 0, moonDay: 1 });

  useEffect(() => {
    const today = new Date();
    const { phase, moonDay } = getMoonPhaseAndDay(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    setMoonData({ phase, moonDay });
  }, []);

  const currentMoonPhase = t(`daily_energy.moon_phases.${String(moonData.phase)}`, {
    returnObjects: true,
  }) as MoonPhaseData;
  const currentMoonDay = t(`daily_energy.days.${String(moonData.moonDay)}`, {
    returnObjects: true,
  }) as MoonDayData;

  const isImagesLoading = usePreloadImages(DAILY_ENERGY_PRELOAD_IMAGES);

  if (isImagesLoading || isSettlementsLoading) return <LoadingSpinner />;

  return (
    <div className="flex h-full flex-col">
      <ScrollArea className="px-5 pb-3">
        <TopBar />

        {currentSlide === 1 && (
          <SlideOneContent
            day={currentMoonDay.day}
            symbolName={currentMoonDay.name}
            dayQuote={currentMoonDay.quote}
            symbolSrc={formatImgSrc(currentMoonDay.symbol.file, 'symbol')}
            moonPhaseName={currentMoonPhase.name}
            moonImageSrc={formatImgSrc(currentMoonDay.day.toString(), 'moon')}
          />
        )}
        {currentSlide === 2 && (
          <SlideTwoContent
            day={currentMoonDay.day}
            dayDescription={currentMoonDay.description}
            symbolIconScr={formatImgSrc(currentMoonDay.symbol.file, 'symbol')}
          />
        )}
        {currentSlide === 3 && (
          <SlideThreeContent
            moonImgSrc={formatImgSrc(currentMoonDay.day.toString(), 'moon')}
            moonPhaseName={currentMoonPhase.name}
            moonPhaseDescription={currentMoonPhase.description}
          />
        )}
      </ScrollArea>

      <CarouselFooter
        slidesCount={SLIDES_COUNT}
        currentSlideIndex={currentSlide - 1}
        onPrev={goToPrevSlide}
        canScrollPrev={canGoToPrevSlide}
        onNext={goToNextSlide}
        canScrollNext={canGoToNextSlide}
      />

      {!!requiredSettlement && (
        <BuySettlementModal
          isOpen={isOpen}
          settlement={requiredSettlement}
          onPurchase={handlePurchase}
          isPurchasingSettlement={isPurchasingSettlement}
          textTKey="daily_energy.buy_modal_text"
        />
      )}
    </div>
  );
}
